import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Google as GoogleIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackPageView, trackUserLogin, trackUserSignup } from '../utils/analyticsUtils';
import { isCoach } from '../utils/adminUtils';
import { createUserProfile } from '../utils/firebaseUtils';

const LoginPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openProfileDialog, setOpenProfileDialog] = useState(false);

  const { signup, login, loginWithGoogle, loginAnonymously, currentUser, userProfile, setUserProfile, approvedCoachUids } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Track page view
  React.useEffect(() => {
    trackPageView('Login Page');
  }, []);

  // Handle redirect after successful login; require name and phone (NTRP and photo only on Profilis)
  React.useEffect(() => {
    if (currentUser && !loading) {
      if (currentUser.isAnonymous) {
        const from = location.state?.from?.pathname || '/rezervacija';
        navigate(from, { replace: true });
        return;
      }
      const needsNameAndPhone = !userProfile || !userProfile.fullName || !userProfile.phoneNumber;
      if (needsNameAndPhone) {
        setFullName(currentUser.displayName || '');
        setEmail(currentUser.email || '');
        setOpenProfileDialog(true);
      } else if (isCoach(currentUser, approvedCoachUids)) {
        navigate('/admin', { replace: true });
      } else {
        const from = location.state?.from?.pathname || '/rezervacija';
        navigate(from, { replace: true });
      }
    }
  }, [currentUser, loading, userProfile, navigate, location.state]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (tabValue === 0) {
        // Login
        await login(email, password);
        trackUserLogin('email');
      } else {
        // Signup
        if (password !== confirmPassword) {
          throw new Error('Slaptažodžiai nesutampa');
        }
        if (password.length < 6) {
          throw new Error('Slaptažodis turi būti bent 6 simbolių');
        }
        if (!fullName.trim()) {
          throw new Error('Vardas ir pavardė yra privalomas');
        }
        if (!phoneNumber.trim()) {
          throw new Error('Telefono numeris yra privalomas');
        }
        
        const profileData = {
          fullName: fullName.trim(),
          phoneNumber: phoneNumber.trim(),
          email: email
        };
        
        await signup(email, password, profileData);
        trackUserSignup('email');
      }
    } catch (error) {
      console.error('Authentication error:', error);
      switch (error.code) {
        case 'auth/user-not-found':
          setError('Vartotojas su tokiu el. paštu nerastas');
          break;
        case 'auth/wrong-password':
          setError('Neteisingas slaptažodis');
          break;
        case 'auth/email-already-in-use':
          setError('Vartotojas su tokiu el. paštu jau egzistuoja');
          break;
        case 'auth/weak-password':
          setError('Slaptažodis per silpnas');
          break;
        case 'auth/invalid-email':
          setError('Neteisingas el. pašto formatas');
          break;
        default:
          setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      await loginWithGoogle();
      trackUserLogin('google');
      
      // Profile completion will be handled by the useEffect when userProfile is loaded
    } catch (error) {
      console.error('Google login error:', error);
      setError('Klaida prisijungiant su Google');
    } finally {
      setLoading(false);
    }
  };

  const handleAnonymousLogin = async () => {
    setError('');
    setLoading(true);
    try {
      await loginAnonymously();
      trackUserLogin('anonymous');
    } catch (error) {
      console.error('Anonymous login error:', error);
      setError('Klaida prisijungiant anonimiškai');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteProfile = async () => {
    if (!fullName.trim() || !phoneNumber.trim()) {
      setError('Vardas ir telefono numeris yra privalomi');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const profileData = {
        fullName: fullName.trim(),
        phoneNumber: phoneNumber.trim(),
        email: currentUser.email
      };
      
      await createUserProfile(currentUser.uid, profileData);
      
      // Update the userProfile in AuthContext immediately
      setUserProfile(profileData);
      
      setOpenProfileDialog(false);
      trackUserSignup('google_profile_completion');
    } catch (error) {
      console.error('Profile completion error:', error);
      setError('Klaida išsaugojant profilį');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseProfileDialog = () => {
    // Don't allow closing if profile is incomplete and user is not anonymous
    if (currentUser && !currentUser.isAnonymous && (!userProfile || !userProfile.fullName || !userProfile.phoneNumber)) {
      setError('Turite užpildyti profilį, kad galėtumėte naudotis sistema');
      return;
    }
    setOpenProfileDialog(false);
    setError(''); // Clear any previous errors
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3, textAlign: 'center', fontWeight: 600 }}>
          Prisijungimas
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Prisijungti" />
          <Tab label="Registruotis" />
        </Tabs>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="El. paštas"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon />
                </InputAdornment>
              ),
            }}
          />

          {tabValue === 1 && (
            <>
              <TextField
                fullWidth
                label="Vardas ir Pavardė"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Telefono numeris"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
                sx={{ mb: 2 }}
                inputProps={{
                  pattern: '[0-9+\\s\\-]*',
                  title: 'Tik skaičiai, + ženklas ir brūkšniai'
                }}
              />
            </>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Slaptažodis</InputLabel>
            <OutlinedInput
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              startAdornment={
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              }
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label="Slaptažodis"
            />
          </FormControl>

          {tabValue === 1 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Patvirtinti slaptažodį</InputLabel>
              <OutlinedInput
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                startAdornment={
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Patvirtinti slaptažodį"
              />
            </FormControl>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ mb: 2 }}
            startIcon={loading ? <CircularProgress size={20} /> : <PersonIcon />}
          >
            {loading ? 'Kraunama...' : (tabValue === 0 ? 'Prisijungti' : 'Registruotis')}
          </Button>
        </Box>

        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">
            ARBA
          </Typography>
        </Divider>

        <Button
          fullWidth
          variant="outlined"
          size="large"
          onClick={handleGoogleLogin}
          disabled={loading}
          startIcon={<GoogleIcon />}
          sx={{ mb: 2 }}
        >
          Prisijungti su Google
        </Button>

        <Button
          fullWidth
          variant="outlined"
          size="large"
          onClick={handleAnonymousLogin}
          disabled={loading}
          startIcon={<PersonIcon />}
          sx={{ mb: 2 }}
        >
          Prisijungti anonimiškai
        </Button>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          Anonimiškai prisijungę galėsite peržiūrėti laisvus laikus, bet negalėsite kurti rezervacijų.
        </Typography>
      </Paper>

             <Dialog 
               open={openProfileDialog} 
               onClose={handleCloseProfileDialog} 
               maxWidth="sm" 
               fullWidth
               disableEscapeKeyDown={currentUser && !currentUser.isAnonymous && (!userProfile || !userProfile.fullName || !userProfile.phoneNumber)}
             >
         <DialogTitle>Užpildykite savo profilį</DialogTitle>
         <DialogContent>
           <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
             Kad galėtumėte kurti rezervacijas, mums reikia jūsų vardo ir telefono numerio. 
             Ši informacija bus automatiškai užpildyta kuriant rezervacijas, bet galėsite ją pakeisti.
           </Typography>
           {error && (
             <Alert severity="error" sx={{ mb: 2 }}>
               {error}
             </Alert>
           )}
          <TextField
            fullWidth
            label="Vardas ir Pavardė"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Telefono numeris"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            required
            sx={{ mb: 2 }}
            inputProps={{
              pattern: '[0-9+\\s\\-]*',
              title: 'Tik skaičiai, + ženklas ir brūkšniai'
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          {currentUser && currentUser.isAnonymous && (
            <Button onClick={handleCloseProfileDialog} color="primary">
              Atšaukti
            </Button>
          )}
          <Button onClick={handleCompleteProfile} color="primary" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Išsaugoti'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LoginPage; 